import _ from 'underscore'

import Api from 'services/api'
import BaseModel from 'models/BaseModel'

import { isPhoneValid, isZipValid, isEmailValid } from 'helpers/validations'
import Apptentive from 'helpers/apptentive'

const defaultCustomer = {
  id: '',
  companyName: '',
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: null,
  phone: '',
  email: '',
  note: '',
  majorAlerts: true,
  criticalAlerts: true,
  sdaAlerts: true,
  companyUpdateAlerts: true,
  notificationRecipientIds: [],
  piiDuplicateAck: false,
  piiAcknowledgedAt: null,
  piiHomeContactFirstName: '',
  piiHomeContactLastName: '',
  piiHomeAddressLine1: '',
  piiHomeAddressLine2: '',
  piiHomeAddressCity: '',
  piiHomeAddressState: '',
  piiHomeAddressPostalCode: '',
  piiHomeContactPhoneNumber: '',
  piiHomeContactEmailAddress: ''
}

class Customer extends BaseModel {
  constructor (options = defaultCustomer) {
    super(options)
    Object.seal(this)
  }

  isNewAndUnacknowledged (customer) {
    return !!(!customer.piiAcknowledgedAt && customer.piiHomeContactId)
  }

  isUpdatedAndUnacknowledged (customer) {
    return !!((customer.piiAcknowledgedAt < customer.piiHomeContactUpdatedAt) || (customer.piiAcknowledgedAt < customer.piiHomeAddressUpdatedAt))
  }

  isDuplicateAndUnacknowledged (customer) {
    return !!(!this.isNewAndUnacknowledged(customer) && !this.isUpdatedAndUnacknowledged(customer) && customer.piiHomeDuplicateNames && !customer.piiDuplicateAck)
  }

  isUpdateAcknowledged (customerAttributes) {
    return !!((customerAttributes.piiAcknowledgedAt < customerAttributes.piiHomeContactUpdatedAt) ||
      (customerAttributes.piiAcknowledgedAt < customerAttributes.piiHomeAddressUpdatedAt))
  }

  getLabelSelectedToRemove (customer, customerAttributes) {
    let labelSelectedToRemove
    if (this.isNewAndUnacknowledged(customer) && customerAttributes.piiAcknowledgedAt) {
      labelSelectedToRemove = 'New'
    } else if (this.isUpdatedAndUnacknowledged(customer) && !this.isUpdateAcknowledged(customerAttributes)) {
      labelSelectedToRemove = 'Updated'
    } else if (this.isDuplicateAndUnacknowledged(customer) && customerAttributes.piiDuplicateAck) {
      labelSelectedToRemove = 'Duplicate'
    }

    return labelSelectedToRemove
  }

  save (dealerId, customerId, updatedCustomerAttributes, onSaveSuccessCallback, onSaveFailCallback, onValidationErrorCallback) {
    const errors = { ...this._validate(updatedCustomerAttributes) }

    if (_.isEmpty(errors)) {
      if (customerId) {
        return Api.Customer.update(dealerId, customerId, updatedCustomerAttributes)
          .then((response) => {
            Apptentive.sendEvent('edit_customer_saved')
            onSaveSuccessCallback(response)
          })
          .catch((e) => {
            onSaveFailCallback(e)
          })
      } else {
        return Api.Customer.create(dealerId, updatedCustomerAttributes)
          .then((response) => {
            Apptentive.sendEvent('customer_added_saved')
            onSaveSuccessCallback(response)
          })
          .catch((e) => {
            onSaveFailCallback(e)
          })
      }
    } else {
      onValidationErrorCallback(errors)
    }
  }

  delete (dealerId, customerId, onDeleteSuccessCallback, onDeleteFailCallback) {
    return Api.Customer.delete(dealerId, customerId)
      .then(() => {
        onDeleteSuccessCallback()
      })
      .catch((e) => {
        onDeleteFailCallback(e)
      })
  }

  _validate (attributes) {
    const errors = {}

    if (!attributes.firstName.trim()) errors['firstName'] = 'First name is required'
    if (!attributes.lastName.trim()) errors['lastName'] = 'Last name is required'
    if (attributes.phone && !isPhoneValid(attributes.phone)) errors['phone'] = 'Invalid phone number'
    if (attributes.zip && !isZipValid(attributes.zip)) errors['zip'] = 'Invalid zip code'
    if (attributes.email && !isEmailValid(attributes.email)) errors['email'] = 'Invalid email'

    return errors
  }
}

export default Customer
